// src/hooks/useSelection.js

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { useSelector, useDispatch } from 'react-redux';
import { throttleRAF } from '../../../utils/throttle';
import {
  addSelectedObjectID,
  clearSelection,
} from '../../../redux/selectionSlice';
import { setActiveLayerName, toggleLayerOnOff, updateLayerProperty } from '../../../redux/layerSlice';
import { layerOffModeRef, layerOffSessionRef, transparencyModeRef, transparencyValueRef, transparencySessionRef } from '../../../components/layout/TopBar/LayerMenu';
import { store } from '../../../redux/store';
import { useSelectedObjectRef } from './useSelectedObjectRef';
import { updateMtextColor, restoreMtextOriginalColors } from '../../../commands/drawMtextFromCode';
import { generateUniqueId } from '../../../utils/generateUniqueId';
import { panelMoveModeRef } from '../../../modules/panel'; // Panel move mode kontrolü
import { wireTitleLockedRef } from '../../gizmo'; // cross-module: selection → gizmo

export function useSelection(scene, camera, renderer,
  ignoreSelectionRef = { current: false }
) {
  const dispatch = useDispatch();
  const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
  const commandType = useSelector((state) => state.operation.commandType);

  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const hoveredObjectRef = useRef(null);
  const hoveredObjectsSet = useRef(new Set()); // Birden fazla hover olan nesneyi takip et
  const hoveredGroupRef = useRef(null);
  const hoveredGroupsSet = useRef(new Set()); // Birden fazla hover olan grubu takip et
  const hoveredBranchIDRef = useRef(null);

  // Seçim dikdörtgeni çizimi için referanslar
  const isDrawingRef = useRef(false);
  const startPointRef = useRef(new THREE.Vector3());
  const rectangleMeshRef = useRef(null);

  const clickedObjectColor = "#ff0000"
  const clickedGroupColor = "#ffff00"
  // Seçim dikdörtgeni içindeki nesneler
  const objectsInsideRef = useRef(new Set());

  // Seçim modu ('inside' veya 'overlap')
  const selectionModeRef = useRef('inside');
  const selectedObjectRef = useSelectedObjectRef();

  function updateLayerFromObject(obj) {
    if (!obj) return;
    const layerName = obj.userData?.layerName
      || obj.parent?.userData?.layerName;
    if (layerName) {
      dispatch(setActiveLayerName(layerName));
    }
  }

  function restoreHoverAppearance(obj) {
    if (!obj) return;
    if (obj.userData?.type === 'mtext') {
      if (obj.userData.originalColor !== undefined) {
        restoreMtextOriginalColors(obj);
      }
    } else if (obj.material && obj.userData.originalColor !== undefined) {
      obj.material.color.set(obj.userData.originalColor);

      // Sadece hatch gibi özel transparency ayarları olan objeler için
      if ((obj.name === 'box-hatch' || obj.name === 'mainPanel-click-hatch' || obj.name === 'panel-click-hatch') && obj.userData.originalOpacity !== undefined) {
        obj.material.opacity = obj.userData.originalOpacity;
        obj.material.transparent = obj.userData.originalTransparent;
      }
    }
  }

  // Bir obje kat planı (isLiveFloorPlanGroup) içeriğinin parçası mı? Kat planı
  // hover'da TÜM grubu cyan boyamasın diye grup-hover'dan hariç tutulur.
  function isFloorPlanContent(obj) {
    let cur = obj;
    while (cur) {
      if (cur.userData?.isLiveFloorPlanGroup) return true;
      cur = cur.parent;
    }
    return false;
  }

  // Tesisat/çizim komutları sırasında kalan hover (cyan) renklerini geri al + hover
  // state'ini sıfırla. Tesisat çizerken kat planı vb. mouseover'da cyan olmasın diye.
  function clearAllHoverColors() {
    const restoreObj = (obj) => {
      if (!obj) return;
      if (obj.userData?.type === 'mtext') {
        if (obj.userData.originalColor !== undefined) restoreMtextOriginalColors(obj);
      } else if (obj.material && obj.userData.originalColor !== undefined) {
        obj.material.color.set(obj.userData.originalColor);
      }
    };
    const restoreGroup = (group) => {
      if (!group) return;
      group.children.forEach((child) => {
        if (child.material && child.userData.originalColor !== undefined) {
          child.material.color.set(child.userData.originalColor);
        }
      });
    };
    restoreObj(hoveredObjectRef.current);
    hoveredObjectsSet.current.forEach(restoreObj);
    restoreGroup(hoveredGroupRef.current);
    hoveredGroupsSet.current.forEach(restoreGroup);
    if (hoveredBranchIDRef.current) {
      const bid = hoveredBranchIDRef.current;
      scene.traverse((child) => {
        if (child.userData?.branchID === bid && (child.isMesh || child.isLine) &&
            child.material?.color && child.userData.originalColor !== undefined) {
          child.material.color.set(child.userData.originalColor);
        }
      });
    }
    hoveredObjectRef.current = null;
    hoveredObjectsSet.current.clear();
    hoveredGroupRef.current = null;
    hoveredGroupsSet.current.clear();
    hoveredBranchIDRef.current = null;
  }


  useEffect(() => {
    if (!renderer || !scene || !scene.children) return;

    // ✅ Line threshold ayarla (wire'ları seçmeyi kolaylaştır)
    raycaster.current.params.Line = { threshold: 5 };

    const handleMouseDown = (event) => {
      // M tuşu basılıysa (panel move mode) seçim işlemini engelle
      if (panelMoveModeRef.current) {
        return;
      }

      // 🔧 wireTitle kilitliyse seçim işlemini engelle
      if (wireTitleLockedRef.current) {
        return;
      }
      
      if (ignoreSelectionRef && ignoreSelectionRef.current) return;
      if (event.button !== 0) return; // Sadece sol tıklamayı işle

      // 🔍 DEBUG ESC: Selection commandType check
 
      if (commandType != null && String(commandType).slice(0, 4) === 'draw') {
         return;
      }


      // Mouse koordinatlarını normalize et
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);

      // Layer Off modu — tıklanan nesnenin katmanını kapat
      if (layerOffModeRef.current) {
        const allObjects = [];
        scene.traverse((obj) => {
          if ((obj.isMesh || obj.isLine || obj.isSprite || obj.isGroup) &&
              obj.userData.isSelectable !== false) {
            allObjects.push(obj);
          }
        });
        const hits = raycaster.current.intersectObjects(allObjects, true);
        if (hits.length > 0) {
          let target = hits[0].object;
          const layerName = target.userData?.layerName
            || target.parent?.userData?.layerName;
          if (layerName) {
            dispatch(toggleLayerOnOff(layerName));
            if (!layerOffSessionRef.current.includes(layerName)) {
              layerOffSessionRef.current.push(layerName);
            }
          }
        }
        renderer.render(scene, camera);
        return;
      }

      // Transparency modu — tiklanan nesnenin katmanina seffaflik uygula
      if (transparencyModeRef.current) {
        const allObjects = [];
        scene.traverse((obj) => {
          if ((obj.isMesh || obj.isLine || obj.isSprite || obj.isGroup) &&
              obj.userData.isSelectable !== false) {
            allObjects.push(obj);
          }
        });
        const hits = raycaster.current.intersectObjects(allObjects, true);
        if (hits.length > 0) {
          let target = hits[0].object;
          const layerName = target.userData?.layerName
            || target.parent?.userData?.layerName;
          if (layerName) {
            // Eski degeri kaydet (ayni katmani tekrar tiklama durumu icin ilk degeri sakla)
            const alreadyTracked = transparencySessionRef.current.find(s => s.layerName === layerName);
            if (!alreadyTracked) {
              // Mevcut layer'in transparency degerini bul
              const layers = store.getState().layer.allLayers;
              const layer = layers.find(l => l.name === layerName);
              const oldValue = layer?.transparency || 0;
              transparencySessionRef.current.push({ layerName, oldValue });
            }
            dispatch(updateLayerProperty({
              layerName,
              property: 'transparency',
              value: transparencyValueRef.current,
            }));
          }
        }
        renderer.render(scene, camera);
        return;
      }

      // Seçim işlemi - tüm sahneyi traverse et (dokümant grupları dahil)
      const objects = [];
      scene.traverse((obj) => {
        if (
          (obj.isMesh || obj.isLine || obj.isSprite || obj.isGroup) &&
          obj.userData.isSelectable !== false &&
          obj !== rectangleMeshRef.current
        ) {
          objects.push(obj);
        }
      });

      let intersects = raycaster.current.intersectObjects(objects, true);

      // 🔧 Selection priority:
      //   wireTitle > tesisat sembolleri > diğer > mtext/text label.
      // mtext label sprite z=0.1 ile panel'in (z=0) üzerine oturuyor; raycaster
      // nearest-first döndüğünden eskiden mtext önce seçiliyordu → panel
      // tıklanamıyor + cyan dashed bbox + 4 yeşil köşe gizmo (mtext selection
      // görseli) çıkıyordu. Tesisat hit'i varsa label sona at; label kendi
      // başına hit ise (üstünde tesisat yoksa) yine seçilebilir.
      if (intersects.length > 1) {
        const TESISAT_PRIORITY_TYPES = new Set([
          'mainPanel', 'electricalPanel', 'supplyPoint',
          'socket', 'LightSwitch', 'LightFixture', 'machine', 'cableShaft',
          'socketBuat', 'buatCircle', 'buatSquare',
          'lightingBuat', 'buatLightingCircle', 'buatLightingSquare',
        ]);
        const LABEL_TYPES = new Set(['mtext', 'text']);
        const PRIORITY = { wireTitle: 0, tesisat: 1, other: 2, label: 3 };
        const classify = (obj) => {
          let cur = obj;
          while (cur) {
            const t = cur.userData?.type;
            if (t === 'wireTitle') return 'wireTitle';
            if (TESISAT_PRIORITY_TYPES.has(t)) return 'tesisat';
            cur = cur.parent;
          }
          return LABEL_TYPES.has(obj?.userData?.type) ? 'label' : 'other';
        };
        intersects = intersects.sort((a, b) => {
          const aP = PRIORITY[classify(a.object)] ?? 2;
          const bP = PRIORITY[classify(b.object)] ?? 2;
          if (aP !== bP) return aP - bP;
          // ✅ Aynı öncelikte: mouse'a perpendicular en yakın olanı seç. DÜNYA uzayında
          // ray.distanceToPoint(intersect.point) kullan — intersect.distanceToRay LOCAL
          // uzayda (parent scale/transform'a göre) hesaplandığı için farklı fpMesh'lerdeki
          // (mesh-FP) wire'lar arasında kıyaslanamıyor, yanlış wire "en yakın" görünüyordu.
          const aD = a.point ? raycaster.current.ray.distanceToPoint(a.point) : (a.distance || 0);
          const bD = b.point ? raycaster.current.ray.distanceToPoint(b.point) : (b.distance || 0);
          return aD - bD;
        });
      }

      if (intersects.length > 0) {
        // handleMouseDown içinde, intersects.length > 0 bloğunda
        let clickedObject = intersects[0].object;

        // 🔧 wireTitle kontrolü - parent'a kadar bak
        let currentCheck = clickedObject;
        while (currentCheck && currentCheck.type !== 'Scene') {
          if (currentCheck.userData?.type === 'wireTitle') {
            clickedObject = currentCheck;
            break;
          }
          currentCheck = currentCheck.parent;
        }
        
        // 🟢 DEBUG: Her tıklamayı logla
         
        // 🟢 DEBUG: Wire tıklandığında konsola yazdır
        const wireTypes = ['wire', 'socketWire', 'lightWire', 'machineWire', 'bellWire'];
 

        if (clickedObject.userData?.type === 'mtext') {
          // id'si yoksa otomatik ata (eski/DXF MTEXT'ler için)
          if (!clickedObject.userData.id) {
            clickedObject.userData.id = generateUniqueId('mtext');
          }
          if (!clickedObject.userData.originalColor) {
            clickedObject.userData.originalColor = clickedObject.userData.color || '#ffffff';
          }
          if (!clickedObject.userData.width) clickedObject.userData.width = clickedObject.scale.x;
          if (!clickedObject.userData.height) clickedObject.userData.height = clickedObject.scale.y;

          if (!selectedObjectIds.includes(clickedObject.userData.id)) {
            dispatch(addSelectedObjectID(clickedObject.userData.id));
          }
          updateMtextColor(clickedObject, '#ff0000');
          updateLayerFromObject(clickedObject);
          return;
        } 
        // 🔥 Panel title kontrolü - sadece drag için, seçilmemeli
        let current = clickedObject;
        while (current) {
          if (current.userData?.isTitle && current.userData?.titleType === 'panel' && current.userData?.isDraggable) {
            // Panel title'a tıklandı - hiçbir şey yapma, sadece drag olsun
            return; // Erken çık, seçim yapma
          }
          current = current.parent;
        }

        // 🔥 wireTitle grubu kontrolü
        current = clickedObject;
        while (current) {
          if (current.userData?.type === 'wireTitle') {
            const groupId = current.userData.id;
            if (!selectedObjectIds.includes(groupId)) {
              dispatch(addSelectedObjectID(groupId));
            }

            // Hover rengi
            current.traverse((child) => {
              if (child.material && child.material.color && !child.userData.originalColor) {
                child.userData.originalColor = child.material.color.getHex();
                child.material.color.set(0x00ffff);
              }
            });


            updateLayerFromObject(currentCheck);
            return; // wireTitle bulundu, diğer seçimleri es geç
          }
          current = current.parent;
        }

        selectedObjectRef.current = clickedObject;


        // ...handleMouseDown içinde...
        if (clickedObject.parent.isGroup) {
          const group = clickedObject.parent;
          const parentGroupId = group.parent?.userData?.id;
          // Grup kendi ID'sine sahipse onu kullan, yoksa parent ID'sini kullan
          const idToSelect = group.userData?.id || parentGroupId;

          // Önce gizmo'ları görünür yap (aynı event'te tıklanabilir olmaları için)
          group.children.forEach((child) => {
            const isGizmo = ['rotateGizmo', 'symetricGizmo', 'stretchGizmo']
              .includes(child.userData?.type);
            if (isGizmo) {
              // Gizmo'ları hemen görünür yap
              child.visible = true;
              return;
            }
          });

          // Sonra seçimi Redux'a ekle ve renkleri değiştir
          if (idToSelect && !selectedObjectIds.includes(idToSelect)) {
            dispatch(addSelectedObjectID(idToSelect));
          }
          updateLayerFromObject(clickedObject);

          hoveredGroupRef.current = group;

          group.children.forEach((child) => {
            // 1) Sadece boyanabilir objelerle ilgilen
            if (!child.isMesh && !child.isLine && !child.isSprite) return;

            // 2) Materyal ve renk var mı diye kontrol et
            const mat = child.material;
            if (!mat) return;

            // (Opsiyonel) Çoklu materyal desteği varsa
            const materials = Array.isArray(mat) ? mat : [mat];

            // 3) Orijinal rengi sakla
            if (!child.userData.originalColor) {
              child.userData.originalColor = materials.map(m =>
                m.color ? m.color.getHex() : null
              );
            }

            // 4) Gizmo'ları atla (zaten yukarıda görünür yapıldı)
            const isGizmo = ['rotateGizmo', 'symetricGizmo', 'stretchGizmo']
              .includes(child.userData?.type);
            if (isGizmo) {
              return;
            }

            // 5) Tüm materyallerin rengini değiştir
            materials.forEach(m => {
              if (m.color) {
                m.color.set(clickedGroupColor);
              }
            });
          });
        } else {
          // Diğer nesneleri seç
          if (!selectedObjectIds.includes(clickedObject.userData.id)) {
            if (!clickedObject.userData.originalColor) {
              clickedObject.userData.originalColor = clickedObject.material.color.getHex();
            }

            // Önce nesneyi orijinal rengiyle Redux'a ekle
            dispatch(addSelectedObjectID(clickedObject.userData.id)); // Nesne ID'si ekleniyor

            // Sonra görsel geri bildirim için rengi kırmızıya çevir
            clickedObject.material.color.set(clickedObjectColor); // Nesne için renk değişimi
            updateLayerFromObject(clickedObject);
          }
        }
      } else {
        // Boş alana tıklandıysa seçim dikdörtgenini başlat
        if (!isDrawingRef.current) {
          // Çizime başla
          isDrawingRef.current = true;

          // Düzlem tanımla (z=0 düzlemi)
          const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
          const intersectPoint = new THREE.Vector3();
          raycaster.current.ray.intersectPlane(plane, intersectPoint);

          if (intersectPoint) {
            startPointRef.current.copy(intersectPoint);

            // Başlangıçta küçük bir dikdörtgen oluştur
            const initialGeometry = new THREE.PlaneGeometry(0.1, 0.1);
            const initialMaterial = new THREE.MeshBasicMaterial({
              color: 0x0000ff,
              transparent: true,
              opacity: 0.25,
            });
            const rectangleMesh = new THREE.Mesh(initialGeometry, initialMaterial);
            rectangleMesh.position.copy(startPointRef.current);
            rectangleMesh.userData.isSelectable = false; // Seçilemez olarak işaretle
            scene.add(rectangleMesh);

            rectangleMeshRef.current = rectangleMesh;
          }
        }
      }

      renderer.render(scene, camera);
    };
    const handleMouseMove = (event) => {
      if (ignoreSelectionRef && ignoreSelectionRef.current) return;

      // 🔧 wireTitle kilitliyse hover işlemini engelle
      if (wireTitleLockedRef.current) {
        return;
      }

      if (commandType != null && String(commandType).slice(0, 4) === 'draw') {
        // Çizim/tesisat komutu aktifken mouseover renk-değişimi (cyan) İSTENMİYOR.
        // Her harekette kalan hover renklerini geri al ve çık → kat planı cyan olmaz.
        clearAllHoverColors();
        return;
      }

      // Mouse koordinatlarını normalize et
      const rect = renderer.domElement.getBoundingClientRect();
      mouse.current.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      mouse.current.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.current.setFromCamera(mouse.current, camera);

      // Eğer seçim dikdörtgeni çiziliyorsa, güncelle
      if (isDrawingRef.current && rectangleMeshRef.current) {
        // Düzlem tanımla (örneğin, z=0 düzlemi)
        const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const intersectPoint = new THREE.Vector3();
        raycaster.current.ray.intersectPlane(plane, intersectPoint);

        if (intersectPoint) {
          const start = startPointRef.current;
          const end = intersectPoint.clone();

          // Dikdörtgenin genişlik ve yüksekliğini hesapla
          const width = end.x - start.x;
          const height = end.y - start.y;

          // Çizim yönüne göre renk belirle
          const isRight = width >= 0;
          const color = isRight
            ? new THREE.Color(0x0000ff) // Mavi
            : new THREE.Color(0x00ff00); // Yeşil

          // Dikdörtgen geometrisini güncelle
          rectangleMeshRef.current.geometry.dispose(); // Eski geometriyi sil
          const safeWidth = Number.isFinite(width) ? Math.abs(width) : 0.1;
          const safeHeight = Number.isFinite(height) ? Math.abs(height) : 0.1;
          rectangleMeshRef.current.geometry = new THREE.PlaneGeometry(safeWidth, safeHeight);


          // Dikdörtgen pozisyonunu güncelle
          rectangleMeshRef.current.position.set(
            start.x + width / 2,
            start.y + height / 2,
            0
          );

          // Dikdörtgen rengini güncelle
          rectangleMeshRef.current.material.color.set(color);

          // Seçim modunu güncelle
          selectionModeRef.current = isRight ? 'inside' : 'overlap';

          // Dikdörtgen sınırlarını hesapla
          const minX = Math.min(start.x, end.x);
          const maxX = Math.max(start.x, end.x);
          const minY = Math.min(start.y, end.y);
          const maxY = Math.max(start.y, end.y);

          // Geçici olarak seçilen nesneleri tutan set
          const currentObjectsInside = new Set();

          // Seçilebilir nesneleri al - tüm sahneyi traverse et (dokümant grupları dahil)
          const objects = [];
          scene.traverse((obj) => {
            if (
              (obj.isMesh || obj.isLine || obj.isSprite || obj.isGroup) &&
              obj.userData.isSelectable !== false &&
              obj !== rectangleMeshRef.current
            ) {
              objects.push(obj);
            }
          });

          objects.forEach((obj) => {
            // Nesne zaten seçiliyse atla
            if (selectedObjectIds.includes(obj.userData.id)) return;
            if (obj.type === "Sprite") return;

            // Nesnenin bounding box'ını hesapla
            const boundingBox = new THREE.Box3().setFromObject(obj);

            if (selectionModeRef.current === 'inside') {
              // Nesne tamamen dikdörtgen içinde mi?
              if (
                boundingBox.min.x >= minX &&
                boundingBox.max.x <= maxX &&
                boundingBox.min.y >= minY &&
                boundingBox.max.y <= maxY
              ) {
                currentObjectsInside.add(obj);

                // Nesne daha önce eklenmemişse
                if (!objectsInsideRef.current.has(obj)) {
                  // Orijinal rengi sakla
                  if (obj.isGroup) {

                    if (!selectedObjectIds.includes(obj.userData.id)) {
                      hoveredGroupRef.current = obj;
                      obj.children.forEach((obje) => {
                        if (obje.material && !obje.userData.originalColor) {
                          obje.userData.originalColor = obje.material.color.getHex();
                        }
                        // Hover rengi camgöbeği olarak ayarla
                        if (obje.material) obje.material.color.set(0x00ffff);  // Camgöbeği rengi
                      })
                    }

                  } else if (obj.userData?.type === 'mtext') { // MTEXT HOVER FIX
                    if (!obj.userData.originalColor) {
                      obj.userData.originalColor = obj.userData.color || '#ffffff';
                    }
                    updateMtextColor(obj, '#00ffff');
                  }
                  else {
                    if (obj.material && !obj.userData.originalColor) {
                      obj.userData.originalColor = obj.material.color.getHex();
                    }
                    // Geçici renk (camgöbeği)
                    if (obj.material) {
                      obj.material.color.set(0x00ffff);
                    }
                  }
                }
              }
            } else if (selectionModeRef.current === 'overlap') {
              const rectBox = new THREE.Box2(
                new THREE.Vector2(minX, minY),
                new THREE.Vector2(maxX, maxY)
              );

              if (
                (obj.geometry &&
                  obj.geometry.attributes &&
                  obj.geometry.attributes.position)
                //  ||obj.isGroup
              ) {

                let intersects = false;
                if (obj.isGroup) {



                } else {
                  const pos = obj.geometry.attributes.position;
                  isIntersect(pos)
                }

                function isIntersect(positions) {
                  for (let i = 0; i < positions.count - 1; i++) {
                    const p1 = new THREE.Vector3().fromBufferAttribute(positions, i);
                    const p2 = new THREE.Vector3().fromBufferAttribute(positions, i + 1);

                    obj.localToWorld(p1);
                    obj.localToWorld(p2);

                    if (doesLineSegmentIntersectBox(p1, p2, rectBox)) {
                      intersects = true;
                      break;
                    }
                  }
                }
                if (intersects) {
                  currentObjectsInside.add(obj);

                  if (!objectsInsideRef.current.has(obj)) {
                    if (obj.isGroup) {
                      if (!selectedObjectIds.includes(obj.userData.id)) {
                        hoveredGroupRef.current = obj;
                        obj.children.forEach((obje) => {
                          if (!obje.userData.originalColor) {
                            obje.userData.originalColor = obje.material.color.getHex();
                          }
                          // Hover rengi camgöbeği olarak ayarla
                          obje.material.color.set(0x00ffff);  // Camgöbeği rengi
                        })
                      }

                    } else if (obj.userData?.type === 'mtext') { // MTEXT HOVER FIX
                      if (!obj.userData.originalColor) {
                        obj.userData.originalColor = obj.userData.color || '#ffffff';
                      }
                      updateMtextColor(obj, '#00ffff');
                    }
                    else {

                      if (obj.material && !obj.userData.originalColor) {
                        obj.userData.originalColor = obj.material.color.getHex();
                      }
                      obj.material.color.set(0x00ffff); // geçici camgöbeği rengi
                    }

                  }

                }
              }
            }

          });

          // Artık dikdörtgen içinde olmayan nesnelerin rengini geri al
          objectsInsideRef.current.forEach((obj) => {
            if (!currentObjectsInside.has(obj)) {
              if (obj.isGroup) {
                obj.children.forEach(obje => {
                  if (obje.userData.originalColor !== undefined) {
                    obje.material.color.set(obje.userData.originalColor);
                  }
                  objectsInsideRef.current.delete(obj);
                })
              } else if (obj.userData?.type === 'mtext') { // MTEXT RESTORE FIX
                if (obj.userData.originalColor !== undefined) {
                  restoreMtextOriginalColors(obj);
                }
                objectsInsideRef.current.delete(obj);
              }
              else {
                if (obj.userData.originalColor !== undefined) {
                  obj.material.color.set(obj.userData.originalColor);
                }
                objectsInsideRef.current.delete(obj);
              }
            }
          });

          // Yeni eklenen nesneleri referans setine ekle
          currentObjectsInside.forEach((obj) => {
            objectsInsideRef.current.add(obj);
          });
        }

        renderer.render(scene, camera);
        return;
      }

      // Normal seçim ve hover işlemleri - tüm sahneyi traverse et (dokümant grupları dahil)
      const objects = [];
      scene.traverse((obj) => {
        if (
          (obj.isMesh || obj.isLine || obj.isGroup) &&
          obj.userData.isSelectable !== false &&
          obj !== rectangleMeshRef.current
        ) {
          objects.push(obj);
        }
      });
      // Kesişimleri bul
      let intersects = raycaster.current.intersectObjects(objects, true);

      // 🔧 Selection priority:
      //   wireTitle > tesisat sembolleri > diğer > mtext/text label.
      // mtext label sprite z=0.1 ile panel'in (z=0) üzerine oturuyor; raycaster
      // nearest-first döndüğünden eskiden mtext önce seçiliyordu → panel
      // tıklanamıyor + cyan dashed bbox + 4 yeşil köşe gizmo (mtext selection
      // görseli) çıkıyordu. Tesisat hit'i varsa label sona at; label kendi
      // başına hit ise (üstünde tesisat yoksa) yine seçilebilir.
      if (intersects.length > 1) {
        const TESISAT_PRIORITY_TYPES = new Set([
          'mainPanel', 'electricalPanel', 'supplyPoint',
          'socket', 'LightSwitch', 'LightFixture', 'machine', 'cableShaft',
          'socketBuat', 'buatCircle', 'buatSquare',
          'lightingBuat', 'buatLightingCircle', 'buatLightingSquare',
        ]);
        const LABEL_TYPES = new Set(['mtext', 'text']);
        const PRIORITY = { wireTitle: 0, tesisat: 1, other: 2, label: 3 };
        const classify = (obj) => {
          let cur = obj;
          while (cur) {
            const t = cur.userData?.type;
            if (t === 'wireTitle') return 'wireTitle';
            if (TESISAT_PRIORITY_TYPES.has(t)) return 'tesisat';
            cur = cur.parent;
          }
          return LABEL_TYPES.has(obj?.userData?.type) ? 'label' : 'other';
        };
        intersects = intersects.sort((a, b) => {
          const aP = PRIORITY[classify(a.object)] ?? 2;
          const bP = PRIORITY[classify(b.object)] ?? 2;
          if (aP !== bP) return aP - bP;
          // ✅ Aynı öncelikte mouse'a perpendicular en yakın — DÜNYA uzayında
          // ray.distanceToPoint(point) (distanceToRay LOCAL olduğu için farklı fpMesh'ler
          // arası kıyaslanamıyordu, yakındaki wire hover'lansın).
          const aD = a.point ? raycaster.current.ray.distanceToPoint(a.point) : (a.distance || 0);
          const bD = b.point ? raycaster.current.ray.distanceToPoint(b.point) : (b.distance || 0);
          return aD - bD;
        });
      }

      if (intersects.length > 0) {

        // 🔧 wireTitle kontrolü - parent'a kadar bak
        let intersectedObject = intersects[0].object;
        let current = intersectedObject;
        while (current && current.type !== 'Scene') {
          if (current.userData?.type === 'wireTitle') {
            intersectedObject = current;
            break;
          }
          current = current.parent;
        }

        // 🔧 Sadece EN YAKIN nesne hover olmalı - diğer TÜM nesneleri temizle
        const objectsToRemove = [];
        hoveredObjectsSet.current.forEach((obj) => {
          // Şu anki en yakın nesne değilse temizle
          if (obj !== intersectedObject) {
            if (selectedObjectIds.includes(obj.userData?.id)) return;

            if (obj.userData?.type === 'mtext') {
              if (obj.userData.originalColor !== undefined) {
                restoreMtextOriginalColors(obj);
              }
            } else if (obj.material && obj.userData.originalColor !== undefined) {
              obj.material.color.set(obj.userData.originalColor);
            }
            objectsToRemove.push(obj);
          }
        });
        objectsToRemove.forEach(obj => hoveredObjectsSet.current.delete(obj));

        // 🔧 Sadece EN YAKIN grubun parent'ı hover olmalı - diğer TÜM grupları temizle
        const currentGroup = intersectedObject.parent?.isGroup ? intersectedObject.parent : null;
        const groupsToRemove = [];
        hoveredGroupsSet.current.forEach((group) => {
          if (group !== currentGroup) {
            if (selectedObjectIds.includes(group.userData?.id)) return;

            group.children.forEach((obj) => {
              if (obj.material && obj.userData.originalColor !== undefined) {
                obj.material.color.set(obj.userData.originalColor);
              }
            });
            groupsToRemove.push(group);
          }
        });
        groupsToRemove.forEach(group => hoveredGroupsSet.current.delete(group));

        // 🔧 branchID olmayan nesneye geçildiğinde eski branchID'yi temizle
        const newBranchID = intersectedObject.userData?.branchID;
        if (!newBranchID && hoveredBranchIDRef.current) {
          const oldBranchID = hoveredBranchIDRef.current;
          scene.traverse((child) => {
            if (child.userData?.branchID === oldBranchID) {
              if (child.isMesh || child.isLine) {
                if (child.material && child.material.color && child.userData.originalColor !== undefined) {
                  child.material.color.set(child.userData.originalColor);
                }
              }
            }
          });
          hoveredBranchIDRef.current = null;
        }
        
        // 🟢 Special handling for mtext hover
        if (intersectedObject.userData?.type === 'mtext') {
          // Skip hover effect if already selected
          if (selectedObjectIds.includes(intersectedObject.userData.id)) {
            if (hoveredObjectRef.current && hoveredObjectRef.current !== intersectedObject) {
              restoreHoverAppearance(hoveredObjectRef.current);
            }
            hoveredObjectRef.current = null;
            renderer.render(scene, camera);
            return;
          }

          // Handle new mtext hover
          if (hoveredObjectRef.current !== intersectedObject) {
            restoreHoverAppearance(hoveredObjectRef.current);

            // Store original color if missing
            if (!intersectedObject.userData.originalColor) {
              const color = intersectedObject.userData.color;
              intersectedObject.userData.originalColor = (typeof color === 'string') ? color : '#ffffff';
            }

            // Apply green hover effect
            updateMtextColor(intersectedObject, '#00ff00');
            hoveredObjectRef.current = intersectedObject;

            renderer.render(scene, camera);
            return;
          }
        }

        const branchID = intersectedObject.userData?.branchID;

 

        // 🔧 Eski branchID farklıysa temizle
        if (hoveredBranchIDRef.current && hoveredBranchIDRef.current !== branchID) {
          const oldBranchID = hoveredBranchIDRef.current;
          scene.traverse((child) => {
            if (child.userData?.branchID === oldBranchID) {
              if (child.isMesh || child.isLine) {
                if (child.material && child.material.color && child.userData.originalColor !== undefined) {
                  child.material.color.set(child.userData.originalColor);
                }
              }
            }
          });
          hoveredBranchIDRef.current = null;
        }

        if (branchID) {
          // Sahnedeki tüm nesneleri tara
          scene.traverse((child) => {
            if (child.userData?.branchID === branchID) {
              if (child.isMesh || child.isLine) {
                if (!child.userData.originalColor && child.material && child.material.color) {
                  child.userData.originalColor = child.material.color.getHex();
                }
                if (child.material && child.material.color) {
                  child.material.color.set(0x00ffff); // Hover rengi: Camgöbeği
                }
              }
            }
          });

          hoveredBranchIDRef.current = branchID;

        } else {
          // Nesne seçili değilse ve hover edilmiyorsa
          if (intersectedObject.parent.isGroup) {
            // 🔧 Grup'a geçildiğinde, önceki tekil nesneyi temizle
            if (hoveredObjectRef.current) {
              const prevObj = hoveredObjectRef.current;
              if (!selectedObjectIds.includes(prevObj.userData?.id)) {
                if (prevObj.userData?.type === 'mtext') {
                  if (prevObj.userData.originalColor !== undefined) {
                    restoreMtextOriginalColors(prevObj);
                  }
                } else if (prevObj.material && prevObj.userData.originalColor !== undefined) {
                  prevObj.material.color.set(prevObj.userData.originalColor);
                }
              }
              hoveredObjectsSet.current.delete(prevObj);
              hoveredObjectRef.current = null;
            }

            const group = intersectedObject.parent
            if (!selectedObjectIds.includes(group.userData.id)) {
              // 🔧 Önceki hover grubunu geri yükle
              if (hoveredGroupRef.current && hoveredGroupRef.current !== group) {
                const prevGroup = hoveredGroupRef.current;
                if (!selectedObjectIds.includes(prevGroup.userData?.id)) {
                  prevGroup.children.forEach((obj) => {
                    if (obj.material && obj.userData.originalColor !== undefined) {
                      obj.material.color.set(obj.userData.originalColor);
                    }
                  });
                }
                hoveredGroupsSet.current.delete(prevGroup);
              }

              // Kat planı grubu → hover'da TÜM grubu cyan boyama (kullanıcı istemiyor;
              // dev kat planı mouseover'da baştan sona cyan oluyordu). Diğer gruplar (panel,
              // tesisat sembolleri vb.) normal hover'lanır.
              if (!isFloorPlanContent(intersectedObject)) {
                hoveredGroupRef.current = group;
                hoveredGroupsSet.current.add(group); // Set'e ekle

                group.children.forEach((obj) => {
                  if (obj.material && obj.material.color && obj.userData.originalColor === undefined) {
                    obj.userData.originalColor = obj.material.color.getHex();
                  }

                  // Hover rengi camgöbeği olarak ayarla
                  if (obj.material && obj.material.color) {
                    obj.material.color.set(0x00ffff); // örnek: camgöbeği rengi
                  }
                })
              }
            }
          } else if (!selectedObjectIds.includes(intersectedObject.userData.id)) {
            // 🔧 Tekil nesneye geçildiğinde, önceki grubu temizle
            if (hoveredGroupRef.current) {
              const prevGroup = hoveredGroupRef.current;
              if (!selectedObjectIds.includes(prevGroup.userData?.id)) {
                prevGroup.children.forEach((obj) => {
                  if (obj.material && obj.userData.originalColor !== undefined) {
                    obj.material.color.set(obj.userData.originalColor);
                  }
                });
              }
              hoveredGroupsSet.current.delete(prevGroup);
              hoveredGroupRef.current = null;
            }

            // 🔧 Önceki hover nesnesini geri yükle
            if (hoveredObjectRef.current && hoveredObjectRef.current !== intersectedObject) {
              const prevObj = hoveredObjectRef.current;
              if (!selectedObjectIds.includes(prevObj.userData?.id)) {
                if (prevObj.userData?.type === 'mtext') {
                  if (prevObj.userData.originalColor !== undefined) {
                    restoreMtextOriginalColors(prevObj);
                  }
                } else if (prevObj.material && prevObj.userData.originalColor !== undefined) {
                  prevObj.material.color.set(prevObj.userData.originalColor);
                }
              }
              hoveredObjectsSet.current.delete(prevObj);
            }

            // Orijinal rengi sakla (material varsa)
            if (intersectedObject.material && intersectedObject.material.color) {
              if (intersectedObject.userData.originalColor === undefined) {
                intersectedObject.userData.originalColor =
                  intersectedObject.material.color.getHex();
              }

              // Hover rengi (camgöbeği) — kat planı içeriği hariç (cyan olmasın)
              if (!isFloorPlanContent(intersectedObject)) {
                intersectedObject.material.color.set(0x00ffff);
              }
            }
            hoveredObjectRef.current = intersectedObject;
            hoveredObjectsSet.current.add(intersectedObject); // Set'e ekle
          } else {
            // Nesne zaten seçiliyse rengini kırmızıya ayarla
            if (!intersectedObject.parent?.isGroup && intersectedObject.material?.color) {
              intersectedObject.material.color.set(clickedObjectColor);
            }
          }
        }
      } else {
        // Kesişim yoksa TÜM hover efektlerini kaldır

        // Tüm hover olan nesneleri temizle
        hoveredObjectsSet.current.forEach((obj) => {
          if (!obj) return;
          if (selectedObjectIds.includes(obj.userData?.id)) return;

          if (obj.userData?.type === 'mtext') {
            if (obj.userData.originalColor !== undefined) {
              restoreMtextOriginalColors(obj);
            }
          } else if (obj.material && obj.userData.originalColor !== undefined) {
            obj.material.color.set(obj.userData.originalColor);

            // Sadece hatch gibi özel transparency ayarları olan objeler için
            if ((obj.name === 'box-hatch' || obj.name === 'mainPanel-click-hatch' || obj.name === 'panel-click-hatch') && obj.userData.originalOpacity !== undefined) {
              obj.material.opacity = obj.userData.originalOpacity;
              obj.material.transparent = obj.userData.originalTransparent;
            }
          }
        });
        hoveredObjectsSet.current.clear();
        hoveredObjectRef.current = null;

        // Tüm hover olan grupları temizle
        hoveredGroupsSet.current.forEach((group) => {
          if (!group) return;
          if (selectedObjectIds.includes(group.userData?.id)) return;

          group.children.forEach((obj) => {
            if (obj.material && obj.userData.originalColor !== undefined) {
              obj.material.color.set(obj.userData.originalColor);

              // Sadece hatch gibi özel transparency ayarları olan objeler için
              if ((obj.name === 'box-hatch' || obj.name === 'mainPanel-click-hatch' || obj.name === 'panel-click-hatch') && obj.userData.originalOpacity !== undefined) {
                obj.material.opacity = obj.userData.originalOpacity;
                obj.material.transparent = obj.userData.originalTransparent;
              }
            }
          });
        });
        hoveredGroupsSet.current.clear();
        hoveredGroupRef.current = null;

        // Branch ID hover'ı temizle
        const branchID = hoveredBranchIDRef.current;
        if (branchID) {
          scene.traverse((child) => {
            if (child.userData?.branchID === branchID) {
              if (child.isMesh || child.isLine) {
                if (child.material && child.material.color && child.userData.originalColor !== undefined) {
                  child.material.color.set(child.userData.originalColor);
                }
              }
            }
          });
          hoveredBranchIDRef.current = null;
        }
      }

      renderer.render(scene, camera);
    };
    function doesLineSegmentIntersectBox(p1, p2, box) {
      // Segment'in tamamı kutunun içindeyse zaten intersect var
      if (box.containsPoint(new THREE.Vector2(p1.x, p1.y)) ||
        box.containsPoint(new THREE.Vector2(p2.x, p2.y))) {
        return true;
      }

      // Dörtgenin kenarlarıyla kesişim kontrolü
      const boxEdges = [
        [new THREE.Vector2(box.min.x, box.min.y), new THREE.Vector2(box.max.x, box.min.y)],
        [new THREE.Vector2(box.max.x, box.min.y), new THREE.Vector2(box.max.x, box.max.y)],
        [new THREE.Vector2(box.max.x, box.max.y), new THREE.Vector2(box.min.x, box.max.y)],
        [new THREE.Vector2(box.min.x, box.max.y), new THREE.Vector2(box.min.x, box.min.y)],
      ];

      const segStart = new THREE.Vector2(p1.x, p1.y);
      const segEnd = new THREE.Vector2(p2.x, p2.y);

      for (const [edgeStart, edgeEnd] of boxEdges) {
        if (doSegmentsIntersect(segStart, segEnd, edgeStart, edgeEnd)) {
          return true;
        }
      }

      return false;
    }
    // İki doğru parçası kesişiyor mu?
    function doSegmentsIntersect(p1, p2, q1, q2) {
      const ccw = (a, b, c) => {
        return (c.y - a.y) * (b.x - a.x) > (b.y - a.y) * (c.x - a.x);
      };
      return ccw(p1, q1, q2) !== ccw(p2, q1, q2) && ccw(p1, p2, q1) !== ccw(p1, p2, q2);
    }
    const handleMouseUp = (event) => {
  if (commandType != null && String(commandType).slice(0, 4) === 'draw') return;
      if (ignoreSelectionRef && ignoreSelectionRef.current) return;
      if (event.button !== 0) return;

      if (isDrawingRef.current && rectangleMeshRef.current) {
        // Seçimi finalize et

        objectsInsideRef.current.forEach((obj) => {
          if (!selectedObjectIds.includes(obj.userData.id)) {
            if (obj.isGroup) {
              obj.children.forEach(obje => {
                if (obje.material && !obje.userData.originalColor) {
                  obje.userData.originalColor = obje.material.color.getHex();
                }
                // ✅ Önce Redux'a orijinal rengiyle kaydet
                dispatch(addSelectedObjectID(obj.userData.id));

                if (obje.material) {
                  obje.material.color.set(clickedGroupColor); // Seçili renk
                }
                // Make gizmos visible for this selected group immediately
                const isGizmo = ['rotateGizmo', 'symetricGizmo', 'stretchGizmo']
                  .includes(obje.userData?.type);
                if (isGizmo) {
                  try { obje.visible = true; } catch (e) {}
                }
              })

            } else if (obj.userData?.type === 'mtext') { // MTEXT SELECTION FIX
              if (!obj.userData.originalColor) {
                obj.userData.originalColor = obj.userData.color || '#ffffff';
              }
              // ✅ Önce Redux'a orijinal rengiyle kaydet
              dispatch(addSelectedObjectID(obj.userData.id));
              updateMtextColor(obj, clickedObjectColor); // Selected color

            }
            else {
              if (obj.material && !obj.userData.originalColor) {
                obj.userData.originalColor = obj.material.color.getHex();
              }
              // ✅ Önce Redux'a orijinal rengiyle kaydet
              dispatch(addSelectedObjectID(obj.userData.id));
              if (obj.material) {
                obj.material.color.set(clickedObjectColor); // Seçili renk
              }
            }

          }
        });

        // Dikdörtgeni kaldır
        scene.remove(rectangleMeshRef.current);
        rectangleMeshRef.current.geometry.dispose();
        rectangleMeshRef.current.material.dispose();
        rectangleMeshRef.current = null;

        isDrawingRef.current = false;
        objectsInsideRef.current.clear();

        renderer.render(scene, camera);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // 🔴 Seçili objeleri geri döndür - tüm sahneyi traverse et (dokümant grupları dahil)
        const allObjects = [];
        scene.traverse((obj) => {
          if (
            (obj.isMesh || obj.isLine || obj.isSprite || obj.isGroup) &&
            obj.userData.isSelectable !== false
          ) {
            allObjects.push(obj);
          }
        });
        allObjects.forEach((obj) => {
          if (selectedObjectIds.includes(obj.userData.id)) {
            if (obj.userData?.type === 'mtext') {
              if (obj.userData.originalColor !== undefined) {
                restoreMtextOriginalColors(obj);
              }
            } else {
              obj.traverse((child) => {
                if (child.material && child.userData?.originalColor !== undefined) {
                  child.material.color.set(child.userData.originalColor);
                }
              });
            }
          }
        });


        dispatch(clearSelection());

        // 🔵 Hover nesnesini de temizle
        if (
          hoveredObjectRef.current &&
          !selectedObjectIds.includes(hoveredObjectRef.current.userData.id)
        ) {
          if (hoveredObjectRef.current.userData.originalColor !== undefined) {
            hoveredObjectRef.current.material.color.set(
              hoveredObjectRef.current.userData.originalColor
            );

            // Sadece hatch gibi özel transparency ayarları olan objeler için
            if ((hoveredObjectRef.current.name === 'box-hatch' || hoveredObjectRef.current.name === 'mainPanel-click-hatch' || hoveredObjectRef.current.name === 'panel-click-hatch') && hoveredObjectRef.current.userData.originalOpacity !== undefined) {
              hoveredObjectRef.current.material.opacity = hoveredObjectRef.current.userData.originalOpacity;
              hoveredObjectRef.current.material.transparent = hoveredObjectRef.current.userData.originalTransparent;
            }
          }
          hoveredObjectRef.current = null;
        }

        // 🔶 Dikdörtgen varsa iptal et
        if (isDrawingRef.current && rectangleMeshRef.current) {
          scene.remove(rectangleMeshRef.current);
          rectangleMeshRef.current.geometry.dispose();
          rectangleMeshRef.current.material.dispose();
          rectangleMeshRef.current = null;
          isDrawingRef.current = false;

          // Geçici renklendirmeyi de sıfırla
          objectsInsideRef.current.forEach((obj) => {
            if (obj.userData.originalColor !== undefined) {
              obj.material.color.set(obj.userData.originalColor);
            }
          });
          objectsInsideRef.current.clear();
        }

        renderer.render(scene, camera);
      }
    };


    const throttledMouseMove = throttleRAF(handleMouseMove);

    // Event listener'ları ekle
    renderer.domElement.addEventListener('mousemove', throttledMouseMove);
    renderer.domElement.addEventListener('mousedown', handleMouseDown);
    renderer.domElement.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup işlemleri
    return () => {
      throttledMouseMove.cancel();
      renderer.domElement.removeEventListener('mousemove', throttledMouseMove);
      renderer.domElement.removeEventListener('mousedown', handleMouseDown);
      renderer.domElement.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('keydown', handleKeyDown);
    };
    }, [
    scene,
    camera,
    renderer,
    selectedObjectIds,
    dispatch,
    ignoreSelectionRef,
    commandType
  ]);



  return null;
};


